import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { Page } from '../../components/page';
import {
  Feature,
  getFeatureByAddOn,
  getProductId,
  PlanInterval,
  SubscriptionSource,
  trueSelfServeAddOn,
} from '../../models';
import { useQuery } from '../../routes';
import { fromOrder, fromSubscription, useAppDispatch } from '../../store';
import { TrueSeatSelect } from './component/true-platform/TrueSeatSelect';
import { TrueBundlePlanSelect } from './component/true-platform/TrueBundlePlanSelect';
import { PriceSummary } from './component/true-platform/PriceSummary';
import { OrderSummary } from './component/true-platform/OrderSummary';
import { TestCloudSession } from './component/true-platform/TestCloudSession';
import { LoadingProgress } from '../../layout';
import { Panel } from '../../components/common/Panel';
import { useGetTrackingCookies, usePlanCartSnapshot, usePreviewTrue } from '../../hook';
import {
  getCycleFromPathParam,
  getPlanProductCycleFromPathParam,
  getQuotaFromPathParam,
  sendTrackingData,
  TrackingPayload,
} from '../../utils/utils';
import OrderDetailSection from './component/OrderDetailSection';
import { BillingToggle } from './component/true-platform/BillingToggle';
import { PromoBanner } from './component/promo-banner/PromoBanner';
import { DEFAULT_TRUE_BUNDLE_FEATURE } from './component/true-platform/trueBundlePlans';
import { useLaunchDarkly } from '../../launchdarkly';

const useStyles = makeStyles(theme => ({
  headerStep: {
    color: '#ffffff',
    fontWeight: 800,
    fontSize: 16,
    letterSpacing: 1,
    textTransform: 'uppercase',
    borderRadius: 12,
  },
  headerLabel: {
    fontWeight: 1000,
    fontSize: 16,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  platformLabel: {
    color: '#5959EB',
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  cloudSessionsRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 2, 0, 2),
    gap: theme.spacing(1),
  },
  cloudIcon: {
    width: 22,
    height: 22,
    backgroundColor: '#5959EB',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
  },
  cloudSessionChip: {
    border: '1px solid #5959EB',
    color: '#5959EB',
    fontWeight: 600,
    fontSize: 12,
    height: 24,
    borderRadius: 4,
  },
  planHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2.5),
    borderBottom: '1px solid #e5e7eb',
  },
  mainCard: {
    border: '2px solid #5959EB',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    boxShadow: '0 0 0 4px rgba(91,95,199,0.09), 0 4px 20px rgba(91,95,199,0.10)',
  },
  mainCardPadding: {
    padding: theme.spacing(2.5),
  },
  orderSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  starterPricingBadge: {
    border: '1px solid #5959EB',
    color: '#5959EB',
    fontWeight: 700,
    fontSize: 10,
    height: 22,
    borderRadius: 4,
  },
}));

const TRUE_SUBSCRIPTION_FEATURES = [
  Feature.TRUE_TEAM,
  Feature.TRUE_ENTERPRISE,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_AUTOMATION,
];

export const PlanV2 = () => {
  const classes = useStyles();
  const { get } = useQuery();
  const { flags } = useLaunchDarkly();
  const dispatch = useAppDispatch();
  const accountId = Number(get('accountId'));
  const trueBundleEnabled = flags?.feTruePlatformAndTrueAutomationEnabled === true;
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const selectAccountSubscriptions = useMemo(
    () => fromSubscription.selectByAccountId(accountId),
    [accountId],
  );
  const accountSubscriptions = useSelector(selectAccountSubscriptions);
  const trueSubscriptions = useMemo(
    () => accountSubscriptions.filter(
      sub => sub.source === SubscriptionSource.RECURLY
        && TRUE_SUBSCRIPTION_FEATURES.includes(sub.feature),
    ),
    [accountSubscriptions],
  );
  const legacyTrueSubscription = trueSubscriptions.find(
    sub => sub.feature === Feature.TRUE_TEAM
      || sub.feature === Feature.TRUE_ENTERPRISE,
  );
  const subscription = legacyTrueSubscription ?? trueSubscriptions[0];
  const trueTeamQuota = getQuotaFromPathParam(Feature.TRUE_TEAM);
  const trueTeamCycle = getCycleFromPathParam(Feature.TRUE_TEAM);
  const truePlatformOnlyCycle = getCycleFromPathParam(Feature.TRUE_PLATFORM_ONLY);
  const defaultTrueBundleCycle = getCycleFromPathParam(DEFAULT_TRUE_BUNDLE_FEATURE);
  const selectedProductCycle = getPlanProductCycleFromPathParam();
  const legacyTrueAddOnQuotas = trueSelfServeAddOn.map(
    addOn => getQuotaFromPathParam(getFeatureByAddOn(addOn)) ?? 0,
  );
  const legacyTrueAddOnsKey = legacyTrueAddOnQuotas.join('|');
  const legacyTrueAddOns = useMemo(
    () => trueSelfServeAddOn.reduce<Record<string, number>>((addOns, addOn, index) => {
      const quota = legacyTrueAddOnQuotas[index];
      if (quota > 0) {
        addOns[addOn.toUpperCase()] = quota;
      }
      return addOns;
    }, {}),
    [legacyTrueAddOnsKey],
  );
  const hasLegacyTruePlatformQuery = trueTeamQuota !== undefined
    || trueTeamCycle !== undefined;
  const useLegacyTruePlatform = !trueBundleEnabled
    && (legacyTrueSubscription !== undefined || hasLegacyTruePlatformQuery);
  const cycle = useLegacyTruePlatform
    ? trueTeamCycle
    : truePlatformOnlyCycle ?? defaultTrueBundleCycle ?? selectedProductCycle;
  const [billingInterval, setBillingInterval] = useState<PlanInterval>(
    subscription?.billingInterval ?? PlanInterval.YEAR,
  );
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const hasPendingOrder = !!pendingOrder && pendingOrder.length > 0;
  const isAnnual = billingInterval === PlanInterval.YEAR;
  const disableBillingToggle = hasPendingOrder
    || (subscription !== undefined && subscription.billingInterval !== PlanInterval.MONTH);
  const disabledActionSx = hasPendingOrder
    ? { pointerEvents: 'none' as const, opacity: 0.5 }
    : undefined;
  const billingToggle = (
    <BillingToggle
      key="billing-toggle"
      isAnnual={isAnnual}
      setBillingInterval={setBillingInterval}
      toggelDisabled={disableBillingToggle}
      showSaveBadge={useLegacyTruePlatform}
      onChangeBillingInterval={interval => trackPlanPageAction({
        field: 'billing_interval',
        object: useLegacyTruePlatform ? Feature.TRUE_TEAM : 'true_bundle',
        action: 'change_interval',
        billing_interval: PlanInterval.getPlanIntervalCode(interval),
      })}
    />
  );

  useEffect(() => {
    dispatch(fromOrder.setAddOns(useLegacyTruePlatform ? legacyTrueAddOns : {}));
  }, [dispatch, useLegacyTruePlatform, legacyTrueAddOns]);

  const loading = useSelector(fromOrder.selectLoading);

  useEffect(() => {
    if (cycle && (!subscription || subscription.billingInterval === PlanInterval.MONTH)) {
      setBillingInterval(cycle);
    }
  }, [cycle]);

  const fetchData = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromOrder.resetOrderPreviewRequest()),
        dispatch(fromOrder.resetOrderPreviewResponse()),
      ]);
    }
  };

  usePreviewTrue();

  const trackPlanPageAction = (payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'plan_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: useLegacyTruePlatform ? 'true_team_legacy' : 'true_bundle',
        ...payload,
        ...trackingCookies,
        cart_snapshot: cartSnapshot,
      },
    );
  };

  useEffect(() => {
    dispatch(fromOrder.doFetchAllOrder(accountId));
  }, [accountId, dispatch]);

  return (
    <Page
      id="plan-v2-page"
      key="plan-v2-page"
      headerId="page.plan.v2.header"
      fetchData={fetchData}
      topBanner={useLegacyTruePlatform ? <PromoBanner accountId={accountId} /> : undefined}
    >
      {pendingOrder && pendingOrder.length > 0
        && (
          <Box
            id="expand-plan-banner"
            display="flex"
            mt={2}
            border="1px solid #dfbc4a"
            borderRadius={2}
            maxWidth={1825}
          >
            <OrderDetailSection pendingOrder={pendingOrder[0]} />
          </Box>
        )}
      <Box
        id="plan-v2-content"
        key="plan-v2-content"
        display="flex"
        py={3}
        sx={theme => ({
          flexDirection: 'row',
          [theme.breakpoints.down('tabletL')]: {
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
        })}
      >
        {/* Left panel */}
        <Panel
          id="plan-v2"
          step={1}
          headerLabel={<FormattedMessage id="page.plan.v2.header" />}
          headerRightItem={billingToggle}
          footer={useLegacyTruePlatform ? (
            <Box sx={disabledActionSx}>
              <TestCloudSession
                key="test-cloud-session"
                billingInterval={billingInterval}
              />
            </Box>
          ) : undefined}
          sx={{
            width: '100%',
            minWidth: '70%',
            maxWidth: 1200,
            pr: 4,
            pb: 4,
            borderRight: '1px solid #BBBBBB',
          }}
        >
          <Box px={1}>
            {useLegacyTruePlatform && (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                mb={2}
                sx={theme => ({
                  gap: theme.spacing(2),
                  [theme.breakpoints.down('tablet')]: {
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                  },
                })}
              >
                <Typography fontWeight={600} lineHeight="135%" fontSize={32} color="#4C5BAA">
                  <FormattedMessage id="page.plan.v2.plan.title" />
                </Typography>
              </Box>
            )}

            <Box sx={disabledActionSx}>
              {/* Number of seats */}
              {useLegacyTruePlatform
                ? (
                  <TrueSeatSelect
                    productId={getProductId(Feature.TRUE_TEAM)}
                    billingInterval={billingInterval}
                  />
                )
                : (
                  <TrueBundlePlanSelect
                    billingInterval={billingInterval}
                  />
                )}

              {/* Pricing row */}
              {useLegacyTruePlatform && (
                <PriceSummary isAnnual={isAnnual} />
              )}
            </Box>
          </Box>
        </Panel>

        {/* Right panel — Order Summary */}
        <Panel
          id="plan-v2-right"
          headerLabel={(
            <Box className={classes.orderSummaryHeader}>
              <FormattedMessage id="page.plan.v2.order.summary.title" />
            </Box>
          )}
          sx={theme => ({
            width: '30%',
            flexShrink: 0,
            [theme.breakpoints.down('tabletL')]: {
              width: '100%',
            },
          })}
          skipTransform
        >
          <OrderSummary isAnnual={isAnnual} />
        </Panel>
      </Box>
      {loading && (<LoadingProgress fullscreen />)}
    </Page>
  );
};
